import React, { useState, useEffect, useMemo } from 'react';
import { generateClient } from 'aws-amplify/data';
import { parseLambdaResponse } from '../../utils/parseLambdaResponse';
import { formatClubDateTime } from '../../utils/clubTime';
import CheckLengthBadge from './CheckLengthBadge';
import ExcludedLengthBadge from './ExcludedLengthBadge';
import CatchStatusLegend, { getCatchCardStatus } from './CatchStatusLegend';
import CatchDetailDialog from './CatchDetailDialog';
import ConfirmActionDialog from './ConfirmActionDialog';
import { PLACED_RANK_LIMIT, isReviewed, mysteryCandidates, type MysteryFish, checkedByLabel } from '@shared/adjudication';
import { buildCategoryRankings, competitorKey } from '@shared/leaderboardRanking';
import { catchRotation, rotatedFrameStyle, rotatedImageStyle } from '../../utils/catchRotation';

const client = generateClient({ authMode: 'userPool' });

const CATEGORIES = [
  { value: 'FLATHEAD', label: 'Flathead' },
  { value: 'BREAM', label: 'Bream' },
  { value: 'JEWFISH', label: 'Jewfish' },
  { value: 'MANGROVE_JACK', label: 'Mangrove Jack' },
  { value: 'WHITING', label: 'Whiting' },
  { value: 'TAILOR', label: 'Tailor' },
  { value: 'TREVALLY', label: 'Trevally' },
  { value: 'MYSTERY', label: 'Mystery Fish 🎣' },
];

interface TopFiveReviewProps {
  submissions: any[];
  eventId: string;
  onRefresh: () => void;
  /** The event's mystery fish, for the same fallback the leaderboard uses. */
  mystery?: MysteryFish | null;
  /** True once results are published: rotation is then refused. */
  published?: boolean;
}

const TopFiveReview: React.FC<TopFiveReviewProps> = ({ submissions, eventId, onRefresh, mystery, published }) => {
  const [activeCategory, setActiveCategory] = useState('FLATHEAD');
  const [photoUrls, setPhotoUrls] = useState<Record<string, string>>({});
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [reasonInput, setReasonInput] = useState<Record<string, string>>({});
  const [reasonErrors, setReasonErrors] = useState<Record<string, string>>({});
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [openCatchId, setOpenCatchId] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState('');
  const [pendingConfirm, setPendingConfirm] = useState<{ catchId: string; decision: 'APPROVED' | 'DISQUALIFIED'; reason?: string } | null>(null);
  const cardRefs = React.useRef<Record<string, HTMLButtonElement | null>>({});

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const approved = useMemo(
    () => submissions.filter((s: any) => s.status === 'APPROVED'),
    [submissions],
  );

  // Top PLACED_RANK_LIMIT entries for the active category, ranked by the
  // SAME function the public leaderboard uses (shared/leaderboardRanking.ts),
  // so this screen and the leaderboard never disagree about who placed.
  // Raised 5 -> 6 on 19 Sep 2026: the club reviews the top 6 of every
  // category, which is the set that can win a place, so that is the set
  // worth verifying. The purpose is confirming fair winners, not policing
  // every entry — disqualification is available but is not the expected
  // outcome.
  const getTopN = () => {
    if (activeCategory === 'MYSTERY') {
      // The mystery fish, matched at read time exactly as the public
      // leaderboard does (#134: nothing is stamped on catches).
      const mysteryCatches = mysteryCandidates(approved as any, mystery) as any[];
      const bestByCompetitor = new Map<string, any>();
      for (const sub of mysteryCatches) {
        const key = competitorKey(sub);
        const existing = bestByCompetitor.get(key);
        if (!existing || sub.lengthCm > existing.lengthCm) {
          bestByCompetitor.set(key, sub);
        }
      }
      return Array.from(bestByCompetitor.values())
        .sort((a: any, b: any) => b.lengthCm - a.lengthCm)
        .slice(0, PLACED_RANK_LIMIT);
    }

    const rankings = buildCategoryRankings(approved as any);
    const entries = (rankings[activeCategory] || []).slice(0, PLACED_RANK_LIMIT);
    const byId = new Map(approved.map((s: any) => [s.id, s]));
    return entries.map((e) => byId.get(e.submissionId)).filter(Boolean);
  };

  const topFive = getTopN();

  // Load photos for top 5
  useEffect(() => {
    const loadPhotos = async () => {
      const urls: Record<string, string> = {};
      for (const sub of topFive) {
        if (sub.photoS3Key && !photoUrls[sub.id]) {
          try {
            const result = await (client.queries as any).generateDownloadUrl({ s3Key: sub.photoS3Key });
            const data = parseLambdaResponse(result?.data);
            if (data?.downloadUrl) urls[sub.id] = data.downloadUrl;
          } catch { /* skip */ }
        }
      }
      if (Object.keys(urls).length > 0) {
        setPhotoUrls((prev) => ({ ...prev, ...urls }));
      }
    };
    loadPhotos();
  }, [topFive.map((s: any) => s.id).join(',')]);

  const handleAdjudicate = async (catchId: string, decision: 'APPROVED' | 'DISQUALIFIED', reasonOverride?: string) => {
    if (actionLoading === catchId) return;
    if (decision === 'DISQUALIFIED') {
      const reason = reasonOverride ?? reasonInput[catchId] ?? '';
      if (!reason.trim()) {
        setReasonErrors((prev) => ({ ...prev, [catchId]: 'A reason is required to disqualify a catch.' }));
        return;
      }
    }
    // A4: a decision made while results are published changes what the public
    // sees, so it is confirmed first. Approve is non-destructive (destructive=false).
    if (published && !pendingConfirm) {
      setPendingConfirm({ catchId, decision, reason: reasonOverride });
      return;
    }
    setPendingConfirm(null);
    const sub = submissions.find((s: any) => s.id === catchId);
    setActionLoading(catchId);
    try {
      const disqualificationReason = decision === 'DISQUALIFIED' ? (reasonOverride ?? reasonInput[catchId] ?? '') : undefined;
      const result = await (client.mutations as any).adjudicateCatch({
        catchId,
        decision,
        disqualificationReason,
        expectedStatus: sub?.status,
      });
      if (result?.errors && result.errors.length > 0) {
        throw new Error(result.errors[0].message);
      }
      // The Lambda reports business failures (already adjudicated, wrong event
      // state, catch not found) in its own payload with a 200 response, so
      // checking result.errors alone would claim "top 6 recalculated" when the
      // entry was never actually settled — leaving a disqualified fish sitting
      // in the top 6 while the admin believes it has been removed.
      const data = parseLambdaResponse<{ success?: boolean; error?: string; message?: string }>(result?.data);
      if (!data?.success) {
        throw new Error(data?.message || data?.error || 'Failed to update entry');
      }
      setReasonErrors((prev) => { const n = { ...prev }; delete n[catchId]; return n; });
      showToast(decision === 'APPROVED' ? 'Entry approved' : 'Entry disqualified, top 6 recalculated', 'success');
      setOpenCatchId((current) => (current === catchId ? null : current));
      onRefresh();
    } catch (err: any) {
      console.error('Error updating entry:', err);
      const message = err.message || 'Failed to update entry';
      showToast(message, 'error');
      setStatusMessage(message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleReinstate = async (catchId: string) => {
    if (actionLoading === catchId) return;
    const sub = submissions.find((s: any) => s.id === catchId);
    setActionLoading(catchId);
    try {
      const result = await (client.mutations as any).adjudicateCatch({
        actionType: 'REINSTATE_CATCH',
        catchId,
        expectedStatus: sub?.status || 'DISQUALIFIED',
      });
      if (result?.errors && result.errors.length > 0) {
        throw new Error(result.errors[0].message);
      }
      const data = parseLambdaResponse<{ success?: boolean; error?: string; message?: string }>(result?.data);
      if (!data?.success) {
        throw new Error(data?.message || data?.error || 'Failed to reinstate the entry');
      }
      showToast('Entry reinstated', 'success');
      setOpenCatchId((current) => (current === catchId ? null : current));
      onRefresh();
    } catch (err: any) {
      console.error('Error updating entry:', err);
      const message = err.message || 'Failed to reinstate the entry';
      showToast(message, 'error');
      setStatusMessage(message);
    } finally {
      setActionLoading(null);
    }
  };

  const closeDialog = (catchId: string) => {
    setOpenCatchId(null);
    setTimeout(() => cardRefs.current[catchId]?.focus(), 0);
  };

  const openSub = openCatchId ? submissions.find((s: any) => s.id === openCatchId) : null;

  return (
    <div>
      {toast && (
        <div role="alert" aria-live="assertive" className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-white text-sm ${
          toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'
        }`}>
          {toast.message}
        </div>
      )}

      <div aria-live="polite" className="sr-only">{statusMessage}</div>

      {pendingConfirm && (
        <ConfirmActionDialog
          title="This changes the published results."
          message="Continue?"
          confirmLabel={pendingConfirm.decision === 'APPROVED' ? 'Approve' : 'Disqualify'}
          destructive={pendingConfirm.decision === 'DISQUALIFIED'}
          onConfirm={() => handleAdjudicate(pendingConfirm.catchId, pendingConfirm.decision, pendingConfirm.reason)}
          onCancel={() => setPendingConfirm(null)}
        />
      )}

      <div className="mb-4">
        <CatchStatusLegend />
      </div>

      {/* Category tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        {CATEGORIES.map((cat) => (
          <button
            key={cat.value}
            onClick={() => setActiveCategory(cat.value)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeCategory === cat.value
                ? 'bg-fishing-blue text-white'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* Top 6 entries */}
      {topFive.length === 0 ? (
        <div className="bg-white shadow rounded-lg p-8 text-center text-gray-500">
          No approved submissions in this category yet.
        </div>
      ) : (
        <div className="space-y-4">
          {topFive.map((sub: any, index: number) => {
            const cardStatus = getCatchCardStatus(sub, index + 1);
            const checkedLabel = isReviewed(sub)
              ? checkedByLabel(sub as any, formatClubDateTime)
              : 'Not checked';
            const showActions = sub.status !== 'DISQUALIFIED' && !isReviewed(sub);
            const openDetail = () => setOpenCatchId(sub.id);
            return (
              <div
                key={sub.id}
                className={`bg-white shadow rounded-lg overflow-hidden flex flex-col sm:flex-row ${cardStatus.borderClass}`}
              >
                {/* Position badge */}
                <div className="flex items-center justify-center w-full sm:w-16 py-2 sm:py-0 bg-gray-50">
                  <span className={`text-2xl font-bold ${
                    index === 0 ? 'text-yellow-500' : index === 1 ? 'text-gray-400' : index === 2 ? 'text-amber-600' : 'text-gray-600'
                  }`}>
                    #{index + 1}
                  </span>
                </div>

                {/* Photo: opens the catch detail (a real button, not the whole card,
                    so the Approve/Disqualify buttons are not nested in it). */}
                <button
                  type="button"
                  ref={(el) => { cardRefs.current[sub.id] = el; }}
                  onClick={openDetail}
                  aria-label={`View catch: ${CATEGORIES.find((c) => c.value === sub.species)?.label || sub.species}, ${sub.lengthCm} cm, ${sub.competitorName || 'Unknown'}`}
                  className="w-full sm:w-32 h-32 flex-shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-fishing-blue focus-visible:ring-inset"
                >
                  {photoUrls[sub.id] ? (
                    <div style={rotatedFrameStyle(catchRotation(sub))} className="w-full h-full overflow-hidden">
                      <img
                        src={photoUrls[sub.id]}
                        alt={`Catch photo: ${CATEGORIES.find((c) => c.value === sub.species)?.label || sub.species}, ${sub.lengthCm} cm`}
                        className="w-full h-full object-cover"
                        style={rotatedImageStyle(catchRotation(sub))}
                      />
                    </div>
                  ) : (
                    <div className="w-full h-full bg-gray-100 flex items-center justify-center text-gray-400 text-xs">
                      View catch
                    </div>
                  )}
                </button>

                {/* Details */}
                <div className="flex-1 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <div className="font-medium text-gray-900">{sub.competitorName || 'Unknown'}</div>
                    <div className="text-lg font-bold text-fishing-blue">
                      {sub.lengthCm}cm <CheckLengthBadge catchItem={sub} className="ml-1 align-middle" />
                      <ExcludedLengthBadge catchItem={sub} className="ml-1 align-middle" />
                    </div>
                    <div className="text-xs text-gray-500">
                      {CATEGORIES.find((c) => c.value === sub.species)?.label || sub.species}
                    </div>
                    <div className="text-sm font-semibold mt-1" style={{ color: cardStatus.state === 'disqualified' ? '#b91c1c' : cardStatus.state === 'needsReview' ? '#b45309' : cardStatus.state === 'checked' ? '#15803d' : '#4b5563' }}>
                      {cardStatus.label}
                    </div>
                    <div className="text-xs text-gray-600">{checkedLabel}</div>
                    {sub.isTopFiveVerified && (
                      <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-800 mt-1">
                        ✓ Verified
                      </span>
                    )}
                  </div>

                  {showActions && (
                    <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="text"
                        placeholder="Reason (required to disqualify)"
                        value={reasonInput[sub.id] || ''}
                        onChange={(e) => {
                          setReasonInput((prev) => ({ ...prev, [sub.id]: e.target.value }));
                          if (reasonErrors[sub.id]) setReasonErrors((prev) => { const n = { ...prev }; delete n[sub.id]; return n; });
                        }}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-fishing-blue"
                      />
                      {reasonErrors[sub.id] && (
                        <p role="alert" className="text-xs font-medium text-red-700">{reasonErrors[sub.id]}</p>
                      )}
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleAdjudicate(sub.id, 'APPROVED')}
                          disabled={actionLoading === sub.id}
                          className="min-h-[44px] px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md disabled:bg-gray-300"
                        >
                          {actionLoading === sub.id ? '...' : 'Approve'}
                        </button>
                        <button
                          onClick={() => handleAdjudicate(sub.id, 'DISQUALIFIED')}
                          disabled={actionLoading === sub.id}
                          className="min-h-[44px] px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md disabled:bg-gray-300"
                        >
                          {actionLoading === sub.id ? '...' : 'Disqualify'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {openSub && (
        <CatchDetailDialog
          catchItem={openSub}
          rank={topFive.findIndex((s: any) => s.id === openSub.id) + 1 || null}
          photoUrl={photoUrls[openSub.id] ?? null}
          onApprove={(catchId) => handleAdjudicate(catchId, 'APPROVED')}
          onDisqualify={(catchId, reason) => handleAdjudicate(catchId, 'DISQUALIFIED', reason)}
          onReinstate={(catchId) => handleReinstate(catchId)}
          actionLoading={actionLoading === openSub.id}
          onClose={() => closeDialog(openSub.id)}
          onRotated={() => onRefresh()}
          competitionPublished={!!published}
        />
      )}
    </div>
  );
};

export default TopFiveReview;
